import get from 'lodash/get';
import api from './api.service';
import queryString from 'query-string';
import {ERROR_SEVERITY} from './constants.service';
import isPlainObject from 'lodash/isPlainObject';
import authService from './auth/auth.service';

import StackTrace from "stacktrace-js"

export async function getErrorStackMapped(error) {
    try {
        const stack = await StackTrace.fromError(error);
        const lines = [error.toString()];

        for (let s = 0; s < stack.length; s++) {
            lines.push([
                '    at ',
                stack[s].getFunctionName() || '<anonymous>', ' (',
                stack[s].getFileName(), ':',
                stack[s].getLineNumber(), ':',
                stack[s].getColumnNumber(), ')',
            ].join(''));
        }

        return lines.join('\n');
    } catch (err) {
        try {
            return error?.stack ?? JSON.parse(error)
        } catch (e) {
        }
    }
}

export function getErrorMessage(error) {

    if (!error || typeof error !== 'object') {
        return error;
    }

    try {
        return error.toString();
    } catch (err) {
        try {
            return error.message;
        } catch (err) {
            return 'Unknown Error';
        }
    }
}

/**
 *
 * @param error
 * @param [data]
 * @param [options]
 * @returns {Promise<AxiosResponse<any>>}
 */
export async function logError(error, data = {}, options = {}) {
    if (process.env.REACT_APP_DISABLE_LOGGING !== "true") {
        let errObj = {};

        if (error) {
            errObj = {
                message: getErrorMessage(error),
                stack: options.stack ?? await getErrorStackMapped(error),
                code: error?.code
            };
        }

        const qs = queryString.stringify({
            severity: options.severity ?? 'error'
        });

        const reqOptions = {};

        try {
            const user = await authService.getCurrentUser();
            const token = await user.getIdToken();

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                };
            }
        } catch(err) {
            // Will log without user info
        }


        return api.post(`/log/mission-x?${qs}`, {
            userAgent: window?.navigator?.userAgent,
            url: window?.location?.href,
            message: data?.message ?? errObj?.message ?? 'No message',
            error: errObj,
            data
        }, true, reqOptions)
    }
}

function logItHandler(message, error, data, severity, options = {}) {
    (async () => {
        try {
            await logError(error, {
                message,
                ...data,
            }, {
                ...options,
                severity,
            });
        } catch(err) {
            try {
                await api.post(`/log/mission-x?severity=error`, {
                    error: {
                        message: err?.message,
                        code: err?.code,
                    },
                    message: 'Failed to log from Web.'
                }, true)
            } catch(err) {
                console.log(err);
            }
        }
    })();
}

export function isError(obj) {
    return Object.prototype.toString.call(obj) === "[object Error]";
}

export const logIt = {
    info: (message, data, options = {}) => {
        if (typeof message === 'object') {
            data = message;
            options = data;
            message = null;
        }
        logItHandler(message, null, data, ERROR_SEVERITY.INFO, options);
    },
    warning: (message, error, data, options = {}) => {
        if (typeof message === 'object') {
            error = message;
            data = error;
            options = data;
            message = null;
        }
        if (!data && !isError(error)) {
            data = error;
            error = null;
        }
        logItHandler(message, error, data, ERROR_SEVERITY.WARNING, options);
    },
    error: (message, error, data, options = {}) => {
        if (typeof message === 'object') {
            error = message;
            data = error;
            options = data;
            message = null;
        }
        if (!data && !isError(error)) {
            data = error;
            error = null;
        }
        logItHandler(message, error, data, ERROR_SEVERITY.ERROR, options);
    },
    critical: (message, error, data, options = {}) => {
        if (typeof message === 'object') {
            error = message;
            data = error;
            options = data;
            message = null;
        }
        if (!data && !isError(error)) {
            data = error;
            error = null;
        }
        logItHandler(message, error, data, ERROR_SEVERITY.CRITICAL, options);
    },
    debug: (message, error, data, options = {}) => {
        if (typeof message === 'object') {
            error = message;
            data = error;
            options = data;
            message = null;
        }
        if (!data && !isError(error)) {
            data = error;
            error = null;
        }
        logItHandler(message, error, data, ERROR_SEVERITY.DEBUG, options);
    },
    alert: (message, error, data, options = {}) => {
        if (typeof message === 'object') {
            error = message;
            data = error;
            options = data;
            message = null;
        }
        if (!data && !isError(error)) {
            data = error;
            error = null;
        }
        logItHandler(message, error, data, ERROR_SEVERITY.ALERT, options);
    },
};